import { Logger } from '@shared/common';
import { FrontendConstants, IsBenignNetworkError, IsBotUserAgent, LobbyClient } from '@shared/frontend';
import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

/** File-local constants for LobbySettingsProvider. */
const localConstants = {
  logContext: 'LobbySettingsProvider',
  abortErrorName: 'AbortError'
} as const;

/** Public lobby settings exposed to the app tree. */
interface ILobbySettingsContext {
  /** Public support contact email from global settings, or empty string when unset/unloaded. */
  contactEmail: string;
  /** Public demo "call us" phone number (E.164) from global settings, or empty string when unset/unloaded. */
  demoE164: string;
  /** Storage key of the platform demo audio file, or empty string when none is uploaded. Used as a presence flag and cache-busting version. */
  audioKey: string;
  /** Storage key of the platform demo video file, or empty string when none is uploaded. Used as a presence flag and cache-busting version. */
  videoKey: string;
}

/** Lobby settings context instance. */
const LobbySettingsContext = createContext<ILobbySettingsContext>({ contactEmail: '', demoE164: '', audioKey: '', videoKey: '' });

/** @returns The public lobby settings context (support contact email plus the public demo media descriptors). */
export function useLobbySettings(): ILobbySettingsContext {
  return useContext(LobbySettingsContext);
}

/**
 * Runs the public IP access gate for the lobby app on first paint and exposes the public lobby settings (support contact email) to the app tree. When the server reports the client IP as blocked,
 * the visitor is redirected to the static 403 page (with the observed IP echoed in the query string). The check fails open: any error simply renders the app so a transient settings failure never
 * locks legitimate visitors out.
 *
 * @param props - Component props.
 * @param props.children - The lobby application tree to render once the gate has been triggered.
 * @returns The provided children wrapped in the lobby settings context.
 */
export function LobbySettingsProvider({ children }: { children: ReactNode }): ReactNode {
  const [contactEmail, setContactEmail] = useState('');
  const [demoE164, setDemoE164] = useState('');
  const [audioKey, setAudioKey] = useState('');
  const [videoKey, setVideoKey] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    LobbyClient.GetLobbySettings(controller.signal)
      .then((access) => {
        if (access.contactEmail) setContactEmail(access.contactEmail);
        if (access.demoE164) setDemoE164(access.demoE164);
        if (access.audioKey) setAudioKey(access.audioKey);
        if (access.videoKey) setVideoKey(access.videoKey);
        if (!access.envAccessBlocked) return;
        const suffix = access.envAccessBlockedIP ? `?${FrontendConstants.AccessDeniedIPParam}=${encodeURIComponent(access.envAccessBlockedIP)}` : '';
        window.location.replace(`${import.meta.env.BASE_URL}${FrontendConstants.AccessDeniedRedirectPath}${suffix}`);
      })
      .catch((err: unknown) => {
        if (Logger.GetErrorName(err) === localConstants.abortErrorName) return;
        // Crawler-aborted or offline network failures are expected noise; record them as logs, not errors.
        if (IsBotUserAgent() || IsBenignNetworkError(err)) Logger.Log(localConstants.logContext, Logger.GetErrorMessage(err));
        else Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
      });
    return () => controller.abort();
  }, []);

  return <LobbySettingsContext.Provider value={{ contactEmail, demoE164, audioKey, videoKey }}>{children}</LobbySettingsContext.Provider>;
}
